import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../hooks/useAuth';

const Header = ({ sidebarCollapsed = false }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const primaryNavItems = [
    {
      label: 'Executive Dashboard',
      path: '/executive-dashboard',
      icon: 'BarChart3'
    },
    {
      label: 'Manager Hub',
      path: '/department-manager-hub',
      icon: 'Users'
    },
    {
      label: 'Staff Center',
      path: '/staff-workflow-center',
      icon: 'CheckSquare'
    },
    {
      label: 'Inventory',
      path: '/inventory-management',
      icon: 'Package'
    },
    {
      label: 'Financial Reports',
      path: '/financial-reports-center',
      icon: 'FileText'
    }
  ];

  const secondaryNavItems = [
    {
      label: 'User Management',
      path: '/user-management',
      icon: 'UserCog'
    }
  ];

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const closeProfile = () => {
    setIsProfileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      closeProfile();
    } catch (error) {
      console.error('Logout failed:', error);
      closeProfile();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef?.current && !profileRef?.current?.contains(event?.target)) {
        closeProfile();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header 
      className={`
        fixed top-0 right-0 h-16 bg-card border-b border-border z-100 transition-smooth
        ${sidebarCollapsed ? 'left-16' : 'left-64'}
        lg:left-0 lg:pl-${sidebarCollapsed ? '16' : '64'}
      `}
    >
      <div className="flex items-center justify-between h-full px-6">
        {/* Primary Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {primaryNavItems?.map((item, index) => (
            <Link
              key={index}
              to={item?.path}
              className={`
                flex items-center px-3 py-2 rounded-md text-sm font-medium transition-smooth
                ${isActiveRoute(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              `}
            >
              <Icon name={item?.icon} size={16} className="mr-2" />
              <span className="hidden lg:inline">{item?.label}</span>
            </Link>
          ))}
          
          {/* More Menu for Secondary Items */}
          {secondaryNavItems?.length > 0 && (
            <div className="relative">
              <button
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
                onClick={() => {/* More menu logic */}}
              >
                <Icon name="MoreHorizontal" size={16} className="mr-2" />
                <span className="hidden lg:inline">More</span>
              </button>
            </div>
          )}
        </nav>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
            aria-label="Notifications"
          >
            <Icon name="Bell" size={20} />
          </button>

          {/* Search */}
          <button
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
            aria-label="Search"
          >
            <Icon name="Search" size={20} />
          </button>

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={toggleProfile}
              className="flex items-center space-x-2 p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
              aria-label="User menu"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <span className="hidden md:inline text-sm font-medium text-foreground">
                {user?.name || 'User'}
              </span>
              <Icon 
                name="ChevronDown" 
                size={16} 
                className={`transition-smooth ${isProfileOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {/* Profile Dropdown */}
            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md dropdown-shadow">
                <div className="py-1">
                  <div className="px-4 py-2 border-b border-border">
                    <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground">{user?.email || ''}</p>
                  </div>
                  
                  <Link
                    to="/profile-settings"
                    onClick={closeProfile}
                    className="w-full flex items-center px-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
                  >
                    <Icon name="Settings" size={16} className="mr-3" />
                    Profile Settings
                  </Link>
                  
                  <div className="border-t border-border">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-smooth"
                    >
                      <Icon name="LogOut" size={16} className="mr-3" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;